<template>
  <section class="user_needs">
    <div class="animation_area">
      <paragraph-header size="2" class="header">{{ $t('pleaseSelectYourItems') }}</paragraph-header>
      <quick-calculator></quick-calculator>
      <section class="products_list_section" v-for="cat in productsOrder" :key="cat">
        <paragraph-header size="3" v-if="productsFromCategory(cat).length > 0">{{ cat }}</paragraph-header>
        <ul v-if="!loadingData">
          <li
            v-for="item in productsFromCategory(cat)"
            :key="item.id"
            :class="{ var_preview: previewItems.indexOf(item.id) > -1 }"
          >
            <h4>{{ item.translated_name || item.name }}</h4>
            <span class="tooltip_header"
              ><span v-if="getImageTitle(item, 1)">{{ getImageTitle(item, 1) }}</span
              >&nbsp;</span
            >
            <figure class="image_wrapper" v-if="getImagePath(item, 0)">
              <img v-lazy="getImagePath(item, 0)" />
              <img class="roll_over" v-if="getImagePath(item, 1)" v-lazy="getImagePath(item, 1)" />
            </figure>

            <div class="bottom_wrapper">
              <product-selector
                :product="item"
                :maxValue="getMaxIterations(item.id)"
                :maxAvailable="getMaxAmount(item.id)"
              ></product-selector>
              <span class="price">
                <span>{{ $t('from') }} {{ item.currency }}&nbsp;</span>
                <transition name="slide-fade" mode="out-in">
                  <span :key="getItemPrice(item.id)">{{ getItemPrice(item.id) | currency(item.currency) }} </span>
                </transition>
              </span>
              <span class="max" v-if="isDev">max:{{ getMaxAmount(item.id) }}</span>
            </div>

            <button
              type="button"
              class="tooltip"
              v-if="getImagePath(item, 1)"
              @mouseenter="showPreview(item.id)"
              @mouseleave="hidePreview(item.id)"
            >
              <svg width="18" height="18" viewbox="0 0 18 18">
                <use xlink:href="#tooltip"></use>
              </svg>
            </button>
          </li>
        </ul>
      </section>
    </div>
    <the-loader v-if="loadingData"></the-loader>
  </section>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import TheLoader from './TheLoader.vue'
import QuickCalculator from '@/components/QuickCalculator.vue'
import ProductSelector from '@/components/reservation/ProductSelector.vue'
import ParagraphHeader from '../atoms/ParagraphHeader.vue'
import Storage from '@/helpers/localStorage'
import CHF from '../../helpers/currency'
import { EUR } from '../../helpers/currency'
import i18n from '../../lang'

export default {
  name: 'ReservationProductList',
  metaInfo: {
    title: i18n.t('Your Products'),
  },
  components: { ProductSelector, TheLoader, QuickCalculator, ParagraphHeader },
  data() {
    return {
      loadingData: true,
      currentCategory: null,
      previewItems: [],
    }
  },
  computed: {
    ...mapState('cart', {
      startDate: 'startDate',
      amountOfWeeks: 'amountOfWeeks',
      cartItems: 'items',
    }),
    ...mapState('products', {
      packages: 'packages',
      storages: 'byStore',
      productsOrder: 'productsOrder',
    }),
    ...mapGetters('products', {
      items: 'baseProductsFilteredByCountry',
    }),
    isDev() {
      return Storage.getIsDev()
    },
    itemsSortByCategory() {
      const arr = [...this.items]
      const ordering = {} // map for efficient lookup of sortIndex
      for (var i = 0; i < this.productsOrder.length; i++) ordering[this.productsOrder[i]] = i

      if (!arr) {
        return []
      } else {
        return arr.sort((a, b) => {
          const categoryNameA = a.properties.category // ignore upper and lowercase
          const categoryNameB = b.properties.category // ignore upper and lowercase
          return ordering[categoryNameA] - ordering[categoryNameB] || categoryNameA.localeCompare(categoryNameB)
        })
      }
    },
    prices() {
      return this.cartItems.map((v) => {
        const product = this.items.find((item) => item.id === v.id)
        return {
          id: v.id,
          price: v.quantity * +product.total_price,
        }
      })
    },
  },
  created() {
    this.fetchData()
    this.clearUnsureData()
  },
  methods: {
    async fetchData() {
      this.loadingData = true
      const params = {
        quantity: 1,
      }

      await this.$store.dispatch('products/getAll', params)
      this.loadingData = false
    },
    clearUnsureData() {
      this.$store.dispatch('cart/changeDeliveryType', null)
    },
    getImagePath(item, index) {
      const imagesObject = item.photos
      const keys = Object.keys(imagesObject)

      if (!keys.length || keys.length - 1 < index) return false

      const photoKey = keys[index]
      const imagePath = imagesObject[photoKey].path

      return imagePath
    },
    getImageTitle(item, index) {
      const imagesObject = item.photos
      const keys = Object.keys(imagesObject)

      if (!keys.length || keys.length - 1 < index) return false

      const photoKey = keys[index]
      const imageTitle = imagesObject[photoKey].title

      return imageTitle
    },
    getItemPrice(id) {
      const productInBasket = this.prices.find((v) => v.id === id)
      if (!productInBasket) {
        const product = this.items.find((v) => v.id === id)
        return +product.total_price * product.properties.quantity_step
      }
      return productInBasket.price
    },
    productsFromCategory(cat) {
      const arr = [...this.items]

      return arr.filter((v) => v.properties.category === cat)
    },
    showPreview(id) {
      if (this.previewItems.indexOf(id) === -1) this.previewItems.push(id)
    },
    hidePreview(id) {
      const index = this.previewItems.indexOf(id)
      if (index > -1) {
        this.previewItems.splice(index, 1)
      }
    },
    getMaxAmount(id) {
      const allPackages = this.packages.filter((v) => v.properties.main_product_id == id)
      const packageWithMaxValue = allPackages.reduce((max, p) =>
        +max.quantity_available > +p.quantity_available ? max : p
      )

      return +packageWithMaxValue.quantity_available
    },
    getMaxIterations(id) {
      const allPackages = this.packages.filter((v) => v.properties.main_product_id == id)
      const packageWithMaxValue = allPackages.reduce((max, p) =>
        +max.quantity_available > +p.quantity_available ? max : p
      )

      const quantityStep = packageWithMaxValue?.properties?.quantity_step || 1

      return Math.floor(packageWithMaxValue.quantity_available / quantityStep)
    },
  },
  watch: {
    startDate() {
      this.fetchData()
    },
    amountOfWeeks() {
      this.fetchData()
    },
  },
  filters: {
    currency(value, currency) {
      if (!value) return ''

      if (currency === 'EUR') {
        return EUR(value)
      }

      return CHF(value)
    },
  },
}
</script>

<style lang="scss" scoped>
section.user_needs {
  @include container;
}

.slide-fade {
  &-enter-active {
    transition: all 0.2s ease;
  }

  &-leave-active {
    transition: all 0.2s ease;
  }

  &-enter {
    transform: translateY(-10px);
    opacity: 0;
  }

  &-leave-to {
    transform: translateY(10px);
    opacity: 0;
  }
}

h3 {
  margin-top: 50px;
}

ul {
  list-style: none;
  margin: 0;
  margin-left: -10px;
  padding: 0;

  display: flex;
  flex-wrap: wrap;

  @include mq($from: tablet) {
    width: calc(100% + 20px);
  }

  li {
    margin: 10px;
    padding: 20px 20px 10px;
    border-radius: 10px;
    background: #e5e1d6;
    text-align: center;
    position: relative;
    flex: 0 0 calc(100% - 10px);

    @include mq($from: tablet) {
      flex: 0 0 calc(50% - 20px);
    }

    @include mq($from: tabletWide) {
      flex: 0 0 calc(33.3% - 20px);
    }

    &.var_preview {
      background-color: $colorWhite;
    }

    h4 {
      @include fontStyleCopyRegular;
      text-align: left;
    }

    img {
      display: block;
      max-width: 100%;
      opacity: 0;
      transition: opacity 0.7s;

      // &[lazy='loading'] {}

      // &[lazy='error'] {}

      &[lazy='loaded'] {
        opacity: 1;
      }
    }
  }
}

.image_wrapper {
  position: relative;
  display: block;
  width: 100%;
  padding: 0;
  padding-bottom: 60%;
  margin: 0;

  img {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
    object-position: center bottom;

    .var_preview & {
      visibility: hidden;
    }

    &.roll_over {
      visibility: hidden;
      z-index: 4;

      .var_preview & {
        visibility: visible;
      }
    }
  }
}

.bottom_wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;

  .selector {
    flex: 0 0 auto;
  }
}

.max {
  padding: 0 5px;
  font-size: 10px;
}

.tooltip {
  outline: none;
  border: 0;
  background: transparent;
  cursor: pointer;
  position: absolute;
  top: 24px;
  right: 20px;
  border: 1px solid $colorBlack;
  border-radius: 50%;

  &:hover {
    background: $colorBlack;
    color: $colorWhite;
  }

  &_header {
    @include fontStyleNavi;
    display: block;
    text-align: left;
    font-size: 12px;
    line-height: calc(20 / 12);
    visibility: hidden;

    .var_preview & {
      visibility: visible;
    }
  }
}

.price {
  @include fontStyleCopyRegular;
  margin-left: 15px;
  margin-bottom: 10px;
  position: relative;
  overflow: hidden;
  min-width: 120px;
  display: inline-block;
  text-align: left;

  span {
    display: inline-block;
  }
}
</style>
